import type { Combo } from '@/combat/combo';
import type { Swing } from '@/combat/swing';
import { WEAPONS, grownWeapon, isWeaponId } from '@/data/weapons';
import type { Facing } from '@/sprites/katie';
import type { ToolBelt } from '@/world/toolbelt';
import type { WeaponId } from '@/data/weapons';

/**
 * What pressing attack does.
 *
 * Lifted out of the frame loop because it is a rule, not wiring: which weapon
 * answers, whether the press is allowed at all, and what the combo step is
 * worth are decisions with edges — and while they lived inside `simulate()`
 * they were in the one file no test imports.
 */

export interface AttackResult {
  /** Which weapon swung, so its use can be credited back to it. */
  readonly weapon: WeaponId | null;
  /** False when nothing happened: no weapon, or a swing already running. */
  readonly started: boolean;
  /**
   * Damage this swing will deal, fixed now rather than read at hit time. A
   * combo that lands late must still pay what it promised when it was thrown.
   */
  readonly damage: number;
}

const NOTHING: AttackResult = { started: false, damage: 0, weapon: null };

export interface AttackOptions {
  readonly belt: ToolBelt;
  readonly swing: Swing;
  readonly combo: Combo;
  readonly facing: Facing;
  /** The weapon's level, so practice actually reaches the swing. */
  readonly level?: number;
}

/**
 * Begin a swing, if one can begin.
 *
 * Reaches for the weapon rather than requiring it in hand: a dedicated attack
 * button that silently does nothing while you hold a hoe reads as broken, and
 * pressing attack is an unambiguous statement about what you want to hold.
 */
export function beginAttack({ belt, swing, combo, facing, level = 1 }: AttackOptions): AttackResult {
  if (swing.isSwinging) return NOTHING;

  // The weapon in hand, if one is; otherwise the best on the belt.
  //
  // Reaching straight for `best` was right while a family held one tool, and
  // wrong the moment two blades could be carried at once: picking the
  // Tidewhisper changed the icon and nothing else, because the swing kept
  // asking for whichever the belt listed first.
  const active = belt.activeTool;
  const weapon = active && isWeaponId(active.id) ? active : belt.best('sword');
  if (!weapon || !isWeaponId(weapon.id)) return NOTHING;

  // Levels are folded in here rather than at the point of damage, so reach and
  // swing time grow too — a practised blade is not merely a harder one.
  const definition = grownWeapon(WEAPONS[weapon.id], level);
  const step = combo.advance();

  if (
    !swing.start(facing, definition.swingSeconds * step.duration, definition.reach * step.reach)
  ) {
    return NOTHING;
  }

  belt.setActive(weapon.id);
  return {
    started: true,
    damage: Math.round(definition.damage * step.damage),
    weapon: weapon.id,
  };
}
